import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../auth.service';
import { catchError, of, Subscription, switchMap, tap } from 'rxjs';
import { FlowComponent } from '../controls/flow/flow.component';

import { MessagesComponent } from '../controls/messages/messages.component';
import { navigateToUrl } from '../utils';
import { SettingsFlow, UpdateSettingsFlowBody } from '../../../sdk/kratos';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-account-settings',
  imports: [
    FlowComponent,
    MessagesComponent,
    RouterLink
  ],
    templateUrl: './account-settings.component.html',
    styleUrl: './account-settings.component.less'
})
export class AccountSettingsComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  flow?: SettingsFlow;

  get enableProfile(): boolean {
    if (!this.flow) {
      return false;
    }
    return this.flow.ui.nodes.filter(node => node.group === 'profile').length > 0;
  }

  get enablePassword(): boolean {
    if (!this.flow) {
      return false;
    }
    return this.flow.ui.nodes.filter(node => node.group === 'password').length > 0;
  }

  get enableOidc(): boolean {
    if (!this.flow) {
      return false;
    }
    return this.flow.ui.nodes.filter(node => node.group === 'oidc').length > 0;
  }

  get unverifiedAddress(): string[] {
    if (!this.flow) {
      return [];
    }
    if (this.flow.identity.verifiable_addresses) {
      return this.flow.identity.verifiable_addresses.filter((addr) => !addr.verified).map((addr) => addr.value);
    } else {
      return [];
    }
  }

  constructor(private activatedRoute: ActivatedRoute,
              private router: Router,
              private authService: AuthService) {

  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    if (this.flow) {
      return;
    }
    const paramMap = this.activatedRoute.snapshot.queryParamMap;
    const flowId = paramMap.get('flow') || undefined;
    const returnTo = paramMap.get('return_to') || undefined;
    this.subscription.add(
      this.authService.createOrGetSettingsFlow(flowId, returnTo)
        .pipe(tap((data) => {
          this.flow = data;
        }), catchError((err: HttpErrorResponse) => {
          return this.authService.handleGetFlowError(err, 'settings', () => {
            this.flow = undefined;
          });
        }))
        .subscribe({
          next: (data) => {},
          error: (err: HttpErrorResponse) => {
            console.log(err);
          }
        })
    );
  }

  onSubmit(values: UpdateSettingsFlowBody): void {
    this.subscription.add(
      this.authService.updateSettingsFlow(values, String(this.flow?.id))
        .pipe(switchMap((data) => {
          this.flow = data;
          // continue_with is a list of actions that the user might need to take before the account-settings update is complete.
          // It could, for example, contain a link to the verification form.
          if (data.continue_with) {
            for (const item of data.continue_with) {
              switch (item.action) {
                case "show_verification_ui":
                  return this.router.navigate(['verification'], {queryParams: {flow: item.flow.id}});
              }
            }
          }

          if (data.return_to) {
            return navigateToUrl(this.router, data.return_to);
          }
          return of(false);
        }), catchError((err: HttpErrorResponse) => {
          return this.authService.handleGetFlowError(err, 'settings', () => {
            this.flow = undefined;
          });
        }))
        .subscribe({
          next: () => {},
          error: (err: HttpErrorResponse) => {
            // If the previous handler did not catch the error it's most likely a form validation error
            if (err.status === 400) {
              // Yup, it is!
              this.flow = err.error as SettingsFlow;
              return;
            }

            console.log(err);
          }
        })
    );
  }

}
